import type { SupabaseClient } from "@supabase/supabase-js";
import type { RuntimeContext } from "../app/runtimeContext";
import { normalizeError } from "../errors/ApplicationError";
import { sanitizeForTelemetry } from "../errors/redaction";
import type { Database } from "../types/database";
import type {
  ObservabilityClient,
  ObservabilityInput,
  ObservabilityLevel,
  ObservabilityRecord,
} from "./types";

function createRecord(
  level: ObservabilityLevel,
  input: ObservabilityInput,
  runtime: RuntimeContext,
): ObservabilityRecord {
  const error =
    input.error instanceof Error || input.error
      ? normalizeError(input.error, {
          eventName: input.eventName,
        }).toTelemetry()
      : undefined;

  return {
    timestamp: new Date().toISOString(),
    level,
    category: input.category ?? "application",
    eventName: input.eventName,
    message: input.message,
    correlationId: input.correlationId ?? runtime.correlationId,
    userId: input.userId ?? runtime.userId,
    sessionId: input.sessionId ?? runtime.sessionId,
    route: input.route ?? runtime.route,
    environment: input.environment ?? runtime.environment,
    productId: input.productId ?? runtime.productId,
    projectId: input.projectId ?? runtime.projectId,
    departmentId: input.departmentId ?? runtime.departmentId,
    agentId: input.agentId ?? runtime.agentId,
    error,
    context: sanitizeForTelemetry(input.context ?? {}) as Record<
      string,
      unknown
    >,
  };
}

abstract class BaseObservabilityClient implements ObservabilityClient {
  constructor(protected readonly runtime: RuntimeContext) {}

  protected abstract write(record: ObservabilityRecord): Promise<void>;

  private async send(level: ObservabilityLevel, input: ObservabilityInput) {
    await this.write(createRecord(level, input, this.runtime));
  }

  debug(input: ObservabilityInput) {
    return this.send("debug", input);
  }
  info(input: ObservabilityInput) {
    return this.send("info", input);
  }
  warn(input: ObservabilityInput) {
    return this.send("warning", input);
  }
  error(input: ObservabilityInput) {
    return this.send("error", input);
  }
  event(input: ObservabilityInput) {
    return this.send(input.level ?? "info", input);
  }
}

export class BrowserObservabilityClient extends BaseObservabilityClient {
  protected async write(record: ObservabilityRecord): Promise<void> {
    if (record.environment !== "development") {
      return;
    }

    const sanitized = sanitizeForTelemetry(record);
    const method =
      record.level === "error" || record.level === "critical"
        ? "error"
        : record.level === "warning"
          ? "warn"
          : "info";
    console[method]("[observability]", sanitized);
  }
}

export class SupabaseObservabilityClient extends BaseObservabilityClient {
  constructor(
    runtime: RuntimeContext,
    private readonly supabase: SupabaseClient<Database>,
  ) {
    super(runtime);
  }

  protected async write(record: ObservabilityRecord): Promise<void> {
    try {
      await (
        this.supabase.from("system_events").insert as unknown as (
          value: Database["public"]["Tables"]["system_events"]["Insert"],
        ) => Promise<{ error: Error | null }>
      )({
        environment: record.environment,
        level: record.level,
        category: record.category,
        event_name: record.eventName,
        message: record.message,
        correlation_id: record.correlationId,
        actor_user_id: record.userId ?? null,
        session_id: record.sessionId ?? null,
        product_id: record.productId ?? null,
        project_id: record.projectId ?? null,
        department_id: record.departmentId ?? null,
        agent_id: record.agentId ?? null,
        route: record.route ?? null,
        metadata: sanitizeForTelemetry({
          error: record.error,
          context: record.context,
        }) as Database["public"]["Tables"]["system_events"]["Insert"]["metadata"],
      });
    } catch {
      // Observability must never crash the application.
    }
  }
}

export class CompositeObservabilityClient implements ObservabilityClient {
  constructor(private readonly clients: ObservabilityClient[]) {}

  private async fanout(action: (client: ObservabilityClient) => Promise<void>) {
    await Promise.allSettled(this.clients.map(action));
  }

  debug(input: ObservabilityInput) {
    return this.fanout((client) => client.debug(input));
  }
  info(input: ObservabilityInput) {
    return this.fanout((client) => client.info(input));
  }
  warn(input: ObservabilityInput) {
    return this.fanout((client) => client.warn(input));
  }
  error(input: ObservabilityInput) {
    return this.fanout((client) => client.error(input));
  }
  event(input: ObservabilityInput) {
    return this.fanout((client) => client.event(input));
  }
}
